'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { Mail, Loader2, ArrowLeft, CheckCircle2, ShieldCheck } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import AppLogo from '@/components/ui/AppLogo';

interface ForgotFormData {
  email: string;
}

export default function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sentEmail, setSentEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<ForgotFormData>();

  const onSubmit = async (data: ForgotFormData) => {
    setLoading(true);
    try {
      const supabase = createClient();
      const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || window.location.origin;
      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${siteUrl}/reset-password`,
      });
      if (error) throw error;
      setSentEmail(data.email);
      setSent(true);
    } catch (err: any) {
      setError('email', {
        type: 'manual',
        message: err?.message || 'Failed to send reset email. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center gap-2 mb-8">
          <AppLogo size={32} />
          <span className="font-bold text-lg text-foreground">LedgerPay</span>
        </div>

        <div className="bg-card border border-border rounded-2xl p-8 shadow-sm">
          {!sent ? (
            <>
              <div className="mb-6">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <ShieldCheck size={22} className="text-primary" />
                </div>
                <h1 className="text-xl font-bold text-foreground">Reset your password</h1>
                <p className="text-sm text-muted-foreground mt-1.5">
                  Enter your work email and we'll send you a secure reset link.
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
                <div>
                  <label htmlFor="forgot-email" className="block text-sm font-semibold text-foreground mb-1.5">
                    Work Email
                  </label>
                  <div className="relative">
                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      id="forgot-email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@company.com"
                      {...register('email', {
                        required: 'Email address is required',
                        pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                      })}
                      className={`w-full pl-10 pr-4 py-2.5 text-sm border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${errors.email ? 'border-danger focus:ring-danger/30' : 'border-input'}`}
                    />
                  </div>
                  {errors.email && <p className="text-xs text-danger mt-1" role="alert">{errors.email.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-bold bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100"
                >
                  {loading ? (
                    <><Loader2 size={16} className="animate-spin" /> Sending reset link...</>
                  ) : (
                    <><Mail size={16} /> Send Reset Link</>
                  )}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={28} className="text-success" />
              </div>
              <h2 className="text-xl font-bold text-foreground mb-2">Check your inbox</h2>
              <p className="text-sm text-muted-foreground mb-1">
                We sent a password reset link to:
              </p>
              <p className="text-sm font-semibold text-foreground mb-5">{sentEmail}</p>
              <div className="p-3 bg-muted/50 rounded-lg text-xs text-muted-foreground mb-6">
                The link expires in 1 hour. Check your spam folder if you don't see it within a few minutes.
              </div>
              <button
                onClick={() => setSent(false)}
                className="text-sm text-primary hover:underline font-medium"
              >
                Try a different email address
              </button>
            </div>
          )}

          <div className="mt-6 pt-5 border-t border-border">
            <Link
              href="/sign-up-login-screen"
              className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft size={14} />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
